/**
 * Configuration metadata for remote modules in Module Federation architecture Each remote module should export a
 * MODULE_META that describes its access requirements
 */
export interface ModuleConfig {
  /** Unique module identifier matching the route path */
  id: string;

  /** Display name for UI (navbar, etc) */
  name: string;

  /** Route path in shell (e.g., 'admin', 'template') */
  path: string;

  /** Icon name or class for navbar display */
  icon: string;

  /** Permission prefixes required to access this module (e.g., ['Admin.*']) */
  requiredPermissions: string[];

  /** Optional description */
  description?: string;

  /** Optional badge or indicator (e.g., 'beta', 'new') */
  badge?: string;
}

/** Module metadata exported from entry.routes.ts This is the single source of truth for module configuration */
export interface ModuleMeta {
  /** Unique module identifier */
  id: string;

  /** Display name */
  name: string;

  /** Base route path in shell (e.g., 'admin', 'template') */
  path: string;

  /** Permission prefixes required to access this module */
  requiredPermissions: string[];

  /** Navigation configuration for the module root */
  navigation: ModuleNavigation;

  /** Optional description */
  description?: string;

  /** User-type-based routing configuration (optional) */
  routing?: UserTypeRoutingConfig;
}

/** Module-level navigation configuration */
export interface ModuleNavigation {
  /** Label shown in navbar */
  label: string;

  /** Icon name for navbar */
  icon: string;

  /** Analytics tracking ID */
  analyticsId?: string;

  /** Sort order in navbar (lower = first) */
  order?: number;

  /** Optional badge (e.g., 'beta', 'new') */
  badge?: string;
}

/** User type enum matching backend UserType table. Each module uses this to declare which user types can access it. */
export enum UserType {
  NursePractitioner = 1,
  ClinicalManager = 2,
  Administrator = 3,
  Auditor = 4,
  AuditManager = 5,
}

/**
 * User-type-based routing configuration for a module. Modules declare which user types should land on them by default.
 * Access control is handled by permission-based guards only.
 */
export interface UserTypeRoutingConfig {
  /** User types for which this module is the default landing page */
  defaultForUserTypes: UserType[];

  /** Default route within the module for each user type */
  defaultRoute?: string;
}

/** Route-level navigation data stored in route.data.nav Use this to mark routes that should appear in the navbar */
export interface RouteNavData {
  /** Label shown in navbar */
  label: string;

  /** Icon name for navbar */
  icon: string;

  /** Whether to show this route in the navbar */
  showInNav: boolean;

  /** Sort order within parent (lower = first) */
  order?: number;

  /** Analytics tracking ID */
  analyticsId?: string;

  /** Optional badge (e.g., 'beta', 'new') */
  badge?: string;

  /** Short description shown as a tooltip in the navigation */
  description?: string;
}

/** Loaded module data from remote MFE */
export interface LoadedModule {
  /** Module metadata */
  meta: ModuleMeta;

  /** Angular routes from entry.routes.ts */
  routes: import('@angular/router').Route[];
}
